import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useSession } from '@supabase/auth-helpers-react';
import { useLocation } from 'react-router-dom';
import { usePostHogAnalytics, ANALYTICS_EVENTS } from '@/hooks/usePostHogAnalytics';
import PerformanceMonitor from './PerformanceMonitor';

interface AnalyticsContextType {
  trackFeatureUsage: (feature: string, action: string, properties?: Record<string, any>) => void;
  trackNavigation: (from: string, to: string) => void;
  trackError: (error: string, context?: Record<string, any>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(undefined);

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const session = useSession();
  const location = useLocation();
  const { trackEvent, identifyUser } = usePostHogAnalytics();

  // Identify user when session changes
  useEffect(() => {
    if (session?.user) {
      identifyUser(session.user.id, {
        email: session.user.email,
        user_id: session.user.id,
        created_at: session.user.created_at
      });
    }
  }, [session, identifyUser]);

  // Track page views
  useEffect(() => {
    trackEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      authenticated: !!session?.user
    });
  }, [location, session, trackEvent]);

  const trackFeatureUsage = (feature: string, action: string, properties?: Record<string, any>) => {
    trackEvent(ANALYTICS_EVENTS.FEATURE_USED, {
      feature,
      action,
      pathname: location.pathname,
      ...properties
    });
  };

  const trackNavigation = (from: string, to: string) => {
    trackEvent(ANALYTICS_EVENTS.NAVIGATION_CLICKED, {
      from,
      to,
      timestamp: Date.now()
    });
  };

  const trackError = (error: string, context?: Record<string, any>) => {
    trackEvent(ANALYTICS_EVENTS.ERROR_OCCURRED, {
      error,
      pathname: location.pathname,
      user_agent: navigator.userAgent,
      timestamp: Date.now(),
      ...context
    });
  };

  const value = {
    trackFeatureUsage,
    trackNavigation,
    trackError
  };

  return (
    <AnalyticsContext.Provider value={value}>
      <PerformanceMonitor routeName={location.pathname} />
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalyticsContext = () => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalyticsContext must be used within an AnalyticsProvider');
  }
  return context;
};